<script setup lang="ts">
import { computed, nextTick, ref, onMounted } from 'vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const { t } = useI18n()
const multiselectRef = ref<InstanceType<typeof Multiselect> | null>(null)

interface Option {
  label: string
  value: string
  id: number
}

const props = defineProps<{
  modelValue: Option[]
  options: Option[]
  placeholder?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: Option[]): void
}>()

const selected = computed({
  get: () => props.modelValue,
  set: (val: Option[]) => emit('update:modelValue', val)
})

const generateListboxId = () => {
  return `multiselect-listbox-${Math.random().toString(36).substr(2, 9)}`
}

const handleOpen = () => {
  nextTick(() => {
    if (multiselectRef.value?.$el instanceof HTMLElement) {
      const input = multiselectRef.value.$el.querySelector('.multiselect__input')
      const listbox = multiselectRef.value.$el.querySelector('.multiselect__content-wrapper')

      if (input && listbox) {
        const listboxId = generateListboxId()

        // Fix aria-controls to reference a valid ID
        input.setAttribute('aria-controls', listboxId)
        listbox.setAttribute('id', listboxId)

        // Fix aria-label to not start with a dash
        const currentLabel = input.getAttribute('aria-label')
        if (currentLabel && currentLabel.startsWith('-')) {
          input.setAttribute('aria-label', props.placeholder ?? t('ui.multiselect.placeholder'))
        }
      }
    }
  })
}

onMounted(() => {
  nextTick(() => {
    if (multiselectRef.value?.$el instanceof HTMLElement) {
      const input = multiselectRef.value.$el.querySelector('.multiselect__input')
      if (input) {
        // Fix initial aria-label if it starts with a dash
        const currentLabel = input.getAttribute('aria-label')
        if (currentLabel && currentLabel.startsWith('-')) {
          input.setAttribute('aria-label', props.placeholder ?? t('ui.multiselect.placeholder'))
        }

        // Remove invalid aria-controls if it points to a non-existent element
        const ariaControls = input.getAttribute('aria-controls')
        if (ariaControls && ariaControls.includes('null')) {
          input.removeAttribute('aria-controls')
        }
      }
    }
  })
})
</script>

<template>
  <Multiselect
    ref="multiselectRef"
    v-model="selected"
    :options="props.options"
    :multiple="true"
    :close-on-select="true"
    :clear-on-select="false"
    :preserve-search="false"
    label="label"
    track-by="value"
    :placeholder="props.placeholder ?? t('ui.multiselect.placeholder')"
    :name="props.placeholder ?? t('ui.multiselect.placeholder')"
    class="w-full"
    :select-label="t('ui.multiselect.select_label')"
    :selected-label="t('ui.multiselect.selected_label')"
    :deselect-label="t('ui.multiselect.deselect_label')"
    :aria-label="props.placeholder ?? t('ui.multiselect.placeholder')"
    @open="handleOpen"
    ><template #tag="{ option, remove }">
      <!-- Library default markup, but without tabindex="1" that breaks the tab order -->
      <span class="multiselect__tag" @mousedown.prevent>
        <span>{{ option.label }}</span>
        <i
          role="button"
          tabindex="0"
          :aria-label="`${t('ui.multiselect.deselect_label')}: ${option.label}`"
          class="multiselect__tag-icon"
          @keydown.enter.prevent="remove(option)"
          @mousedown.prevent="remove(option)"
        />
      </span> </template
    ><template #noResult>
      {{ $t('common.nothing_found') }}
    </template></Multiselect
  >
</template>

<style scoped>
:deep(.multiselect) {
  @apply rounded-md text-sm ring-offset-2 focus-within:ring-2 focus-within:ring-blue-600;
}

:deep(.multiselect__placeholder) {
  @apply text-gray-500;
}

:deep(.multiselect__tags) {
  @apply rounded-md pl-4;
}

:deep(.multiselect__tag) {
  @apply rounded-md bg-blue-600 py-1 pl-2 pr-5 text-base text-white;
}
:deep(.multiselect__tag-icon:after) {
  @apply absolute right-1.5 top-0.5 text-xl text-white;
}
:deep(.multiselect__option--highlight) {
  @apply bg-blue-600 text-white;
}
</style>
